import { get, noop } from 'lodash';
import { proto, proto as vibeProto, spottingConfigToProto } from '../../proto/AsrProto';
import { cdLogger, recLogger } from '../../utils/logger/loggerFactory';
import { getAsrEndpoint } from '../../utils/config';
import { Asr } from '../../errors/Errors';
import { captureException } from '../../tracker/raven';
import { checkForBigerThanInt32, guid, isValidEndpoint } from '../../utils/background/general';
import { getEnabledASRVendor } from '../../store/selectors/asr';
import { updateQueryStringParameter } from '../../utils/url';
import { IS_ELECTRON_APP_WITH_RECORDING, IS_LEGACY_LISTENER_MODE, IS_UI_ONLY_MODE } from '../../config/electron';
import EventEmitter from 'eventemitter3';
import { createAsrWsConnection } from '../WsConnection';
import { CHANNEL, EXPLICIT_INTEGRATION_SOURCE, wsConnectionClosingCodes } from '../../constants/backend';
import type {
    AsrRecognitionConfig,
    AuthSystemInfo,
    AuthUserData,
    BackendAppConfigAbsoluteSilenceDefinition,
    BackendAppConfigCallDetection
} from '../../store/types';

const holdEvents = {
    CALL_HOLD: 'call_hold',
    CALL_UNHOLD: 'call_unhold'
};

const WS_CLOSE_TIMEOUT = 5000;

const startEvent = 'CALL_START';

interface LoggerLike {
    log: (...args: unknown[]) => void;
    error: (...args: unknown[]) => void;
    debug: (...args: unknown[]) => void;
}

interface StopOptions {
    acdSessionOffsetMsec: number;
    asrSessionOffsetMsec: number;
    reason?: number;
    code?: number;
}

type StartConfig = {
    jwt?: string | null;
    userData?: AuthUserData;
} & Record<string, unknown>;

interface SessionConfigInput {
    sampleRateHertz?: number;
    absoluteSilenceDefinition?: BackendAppConfigAbsoluteSilenceDefinition;
    saveStream?: boolean;
    systemInfo?: AuthSystemInfo;
    enableCallMetadataOnly?: boolean;
    callDetection?: BackendAppConfigCallDetection;
}

interface RecognitionConfigInput {
    languageCode?: string;
    lingwareTopic?: string;
    asr?: AsrRecognitionConfig;
    sampleRateHertz?: number;
    systemInfo?: AuthSystemInfo;
    enableAnonymization?: boolean;
    enableAsrDebug?: boolean;
    enableCallMetadataOnly?: boolean;
    incallRecognitionSessionNumber?: number;
    callId?: string | number;
}

type SendRecognitionOptions = RecognitionConfigInput & {
    spottingConfig?: unknown;
};

const cdLog = cdLogger as LoggerLike;
const recLog = recLogger as LoggerLike;

export default class AsrConnection extends EventEmitter {
    private _name: string;

    private _ready: boolean;

    private _ws: ReturnType<typeof createAsrWsConnection> | null;

    private _sessionConfig: unknown;

    private _jwt: string | null;

    private _logger: LoggerLike;

    private _started: boolean;

    private _id: string;

    private _callId: string | number | null;

    private _stopRequestedByClient: boolean;

    private _stopCode: number | undefined;

    private _forceCloseTimeout: ReturnType<typeof setTimeout> | null;

    private _serviceFinalized: boolean;

    private _allowSendWhileStopping: boolean;

    constructor(name: string) {
        super();

        this._name = name;
        this._ready = false;
        this._ws = null;
        this._sessionConfig = null;
        this._jwt = null;
        this._logger = cdLog;
        this._started = false;
        this._id = '';
        this._callId = null;
        this._stopRequestedByClient = false;
        this._stopCode = undefined;
        this._forceCloseTimeout = null;
        this._serviceFinalized = false;
        this._allowSendWhileStopping = false;
    }

    start(config: StartConfig) {
        if (this._started) {
            const error = new Asr.AlreadyStartedError({
                id: this._id,
                name: this._name
            });
            captureException(error);
            this._logger.error(error.message, error);
            return;
        }

        this._started = true;
        this._id = guid();
        this._logger.log(`${this._prefix()}:: start`);

        const { jwt, ...restConfig } = config;

        if (!jwt) {
            const error = new Asr.JwtRequiredError({
                id: this._id,
                name: this._name
            });
            captureException(error);
            this._logger.error(error.message, error);
            return;
        }

        const agentID = config.userData?.userId ?? 0;

        this._jwt = jwt;
        this._sessionConfig = this.buildSessionConfig(restConfig as SessionConfigInput);
        if (!this._sessionConfig) {
            return;
        }
        const meta = {
            description: 'Vibe connection',
            name: this._name,
            id: this._id
        };
        this._ws = createAsrWsConnection({
            ...this._getEndpoint(restConfig, agentID, this._id),
            meta
        });
        this._ws.on('opened', this._baseHandleOpen);
        this._ws.on('closed', this._handleClose);
        this._ws.on('message', (data: unknown) => {
            const msg = proto.RecognizeResponse.decode(data);
            if (msg) {
                if (msg.serviceMessage) {
                    const status = vibeProto.ServiceMessageStatus[msg.serviceMessage.status];
                    this._logger.log('service message:', status, msg.serviceMessage);
                }
                this._handleMessage(msg);
            }
        });
    }

    isOpened() {
        return this._ready;
    }

    processChunk(chunk: unknown) {
        return this._send({
            streamingRequest: {
                audioChunkWithMetadata: chunk
            }
        });
    }

    sendMeta(meta = {}) {
        this._send({
            streamingRequest: {
                serviceMessage: {
                    status: proto.ServiceMessage.Status.OK,
                    serviceIsFinalized: false,
                    metainfo: [],
                    callMetainfo: meta
                }
            }
        });
    }

    sendChannelControlRequest(channel: number, forgetAllData = false, forgetAudioOnly = false) {
        const req = {
            channelControl: {
                channel: channel === CHANNEL.MICROPHONE ? proto.Channel.MICROPHONE : proto.Channel.SYSTEM,
                operation: forgetAllData
                    ? proto.ChannelControl.Operation.FORGET_ALL_DATA
                    : forgetAudioOnly
                      ? proto.ChannelControl.Operation.FORGET_AUDIO
                      : proto.ChannelControl.Operation.FORGET_AUDIO_AND_TRANSCRIPT
            }
        };
        this._send(req);
    }

    _prefix() {
        return `(${this._name}:${this._id})`;
    }

    _sendRequest(obj: unknown) {
        if (this._stopRequestedByClient && !this._allowSendWhileStopping) {
            return;
        }

        if (!this._ws || !this._ws.isOpen()) {
            const error = new Asr.CantSendRequestError({
                id: this._id,
                name: this._name
            });
            this._logger.error(error.message, error);
            captureException(error);
            return error;
        }
        try {
            this._ws.send(obj);
            return { bufferedAmount: this.getBufferedAmount() };
        } catch (error) {
            captureException(error);
            this._logger.error((error as Error).message, error);
            return error;
        }
    }

    getBufferedAmount() {
        const getBufferedAmount = this._ws?.getBufferedAmount;

        return typeof getBufferedAmount === 'function' ? getBufferedAmount.call(this._ws) : 0;
    }

    _finalize({ acdSessionOffsetMsec, asrSessionOffsetMsec, reason = proto.FinalizationReason.CALL_END_NORMAL }: StopOptions) {
        if (!this._ws || !this._ws.isOpen()) {
            // Close socket if it is in CONNECTING, CLOSING or CLOSED state.
            // close() is idempotent.
            const msg = `Vibe:${this._id}; Reason:${proto.FinalizationReason[reason]}`;
            this._ws?.close(wsConnectionClosingCodes.DEFAULT, msg);
            const error = new Asr.CantFinalizeError({
                id: this._id,
                name: this._name
            });
            // CONNECTING/CLOSING are expected races (stop() before WS opened); only the CLOSED
            // state indicates a real bug worth tracking.
            if (!this._ws || this._ws.isClosed()) {
                captureException(error);
            }
            this._logger.error(error.message, error);
            return;
        }

        const request = {
            streamingRequest: {
                finalizationToken: {
                    acdSessionOffsetMsec,
                    asrSessionOffsetMsec,
                    reason
                }
            }
        };

        this._allowSendWhileStopping = true;
        this._send(request);
        this._allowSendWhileStopping = false;
        this._logger.log(`${this._prefix()}:: finalized`, request);

        if (this._forceCloseTimeout) {
            clearTimeout(this._forceCloseTimeout);
        }
        this._forceCloseTimeout = setTimeout(() => {
            this._logger.log(`${this._prefix()}:: force close by timeout`);
            this._ws?.close(wsConnectionClosingCodes.DEFAULT);
        }, WS_CLOSE_TIMEOUT);
    }

    _baseHandleOpen = () => {
        this._logger.log(`${this._prefix()}:: Socket open!`);
        if (!this._started || this._stopRequestedByClient) {
            this._logger.log(`${this._prefix()}:: Connection already closed and discarded!`);
            this._ws?.close(this._stopCode ?? wsConnectionClosingCodes.DEFAULT);
            return;
        }

        const jwtConf = {
            authRequest: { mechanismName: proto.MechanismName.JWT, authData: this._jwt }
        };

        this._send(jwtConf);

        if (IS_LEGACY_LISTENER_MODE || IS_ELECTRON_APP_WITH_RECORDING) {
            this._send(this._sessionConfig);
        }

        this._logger.log(`${this._prefix()}:: initial config send`, this._sessionConfig);
        this._handleOpen();
        this._ready = true;
        this.emit('started', {
            name: this._name,
            id: this._id
        });
    };

    _handleClose = (event: { code?: number; wasClean?: boolean; reason?: string }) => {
        const { code, wasClean, reason } = event;
        const { _id: id, _name: name } = this;
        const closedByClient = this._stopRequestedByClient;
        const stopCode = this._stopCode;

        if (this._forceCloseTimeout) {
            clearTimeout(this._forceCloseTimeout);
            this._forceCloseTimeout = null;
        }

        // TOREVIEW: @yury @evgeniy only create ASR Closed Error if wasClean === false
        if (!wasClean) {
            const error = new Asr.ClosedError({
                id,
                name,
                code,
                reason,
                wasClean
            });

            this._logger.error(error.message, error);
        }

        if (this._ws) {
            this._ws.removeAllListeners();
        }
        this._baseReset();
        this._reset();

        this.emit('stopped', {
            closedByClient,
            wasClean,
            code: stopCode ?? code
        });
    };

    stop({ acdSessionOffsetMsec, asrSessionOffsetMsec, reason, code }: StopOptions) {
        if (this._ws) {
            this._logger.log(`${this._prefix()}:: stop`);

            this._ready = false;
            this._stopRequestedByClient = true;
            this._stopCode = code;

            // If something goes wrong while connection is closed then we get a weird offset,
            // link to the bug below.
            // https://i2x-gmbh.atlassian.net/browse/DEVELOP-3144
            const acdSessionOffsetMsecCheck = checkForBigerThanInt32(acdSessionOffsetMsec) ? 0 : acdSessionOffsetMsec;
            const asrSessionOffsetMsecCheck = checkForBigerThanInt32(asrSessionOffsetMsec) ? 0 : asrSessionOffsetMsec;
            this._finalize({
                acdSessionOffsetMsec: acdSessionOffsetMsecCheck,
                asrSessionOffsetMsec: asrSessionOffsetMsecCheck,
                reason
            });
        } else {
            // TODO: in future this could be OK
            const error = new Asr.CantStopError({
                id: this._id,
                name: this._name
            });
            captureException(error);
            this._logger.error(error.message, error);
        }
    }

    _baseReset() {
        this._sessionConfig = null;
        this._jwt = null;
        this._started = false;
        this._ready = false;
        this._stopRequestedByClient = false;
        this._stopCode = undefined;
        this._forceCloseTimeout = null;
        this._serviceFinalized = false;
        this._allowSendWhileStopping = false;
    }

    _getEndpoint(config: Record<string, unknown>, agentID: string | number = 0, callId: string | number | null = null) {
        const endpoint = getAsrEndpoint();
        let { host } = endpoint;
        const { protocol } = endpoint;

        const asrEndpoint = get(config, 'asr.endpoint');
        if (typeof asrEndpoint === 'string' && isValidEndpoint(asrEndpoint)) {
            host = asrEndpoint;
        }

        host = updateQueryStringParameter(host, 'user_id', String(agentID));
        if (callId) {
            host = updateQueryStringParameter(host, 'call_id', String(callId));
        }

        return {
            protocol,
            host
        };
    }

    getId() {
        return this._id;
    }

    buildSessionConfig({
        sampleRateHertz,
        absoluteSilenceDefinition,
        saveStream,
        systemInfo,
        enableCallMetadataOnly,
        callDetection: { integrationMethod, explicitIntegrationSource, optInGracePeriodMsec, explicitPingPeriodMsec, appWhitelist } = {}
    }: SessionConfigInput) {
        const hasOpus = Boolean((window as Window & { hasOpus?: boolean }).hasOpus);
        let encoding = hasOpus ? vibeProto.AudioEncoding.OPUS : vibeProto.AudioEncoding.FLAC;
        if (process.env.NODE_ENV === 'development' || process.env.NODE_ENV === 'test') {
            encoding = hasOpus ? vibeProto.AudioEncoding.OPUS : vibeProto.AudioEncoding.LINEAR16;
        }
        if (IS_ELECTRON_APP_WITH_RECORDING) {
            encoding = vibeProto.AudioEncoding.OPUS;
        }

        return {
            detectionConfig: {
                saveStream,
                sampleRate: sampleRateHertz,
                sessionId: this._id,
                absoluteSilenceDefinition,
                systemInfo,
                encoding,
                integrationMethod,
                explicitIntegrationSource: [
                    EXPLICIT_INTEGRATION_SOURCE.KEYPHRASE_AUDIO_EVENT,
                    EXPLICIT_INTEGRATION_SOURCE.RECORDER_EVENT
                ].includes(Number(explicitIntegrationSource ?? -1))
                    ? EXPLICIT_INTEGRATION_SOURCE.I2X_MANUAL_CALL_EVENT
                    : explicitIntegrationSource,
                optInGracePeriodMsec,
                explicitPingPeriodMsec,
                appWhitelist,
                enableCallMetadataOnly
            }
        };
    }

    _send(obj: unknown) {
        return this._sendRequest(obj);
    }

    sendForwardedEvent(uctRequest: unknown) {
        cdLog.debug('forwarded event:', uctRequest);
        // Forwarding integration events to Vibe is currently deprecated.
        // this._sendRequest({ streamingRequest: { uctRequest } });
    }

    buildRecognitionConfig({
        languageCode,
        lingwareTopic,
        asr,
        sampleRateHertz,
        systemInfo,
        enableAnonymization,
        enableAsrDebug,
        enableCallMetadataOnly,
        incallRecognitionSessionNumber,
        callId
    }: RecognitionConfigInput) {
        this._callId = callId ?? null;
        const hasOpus = Boolean((window as Window & { hasOpus?: boolean }).hasOpus);
        let encoding = hasOpus ? vibeProto.AudioEncoding.OPUS : vibeProto.AudioEncoding.FLAC;
        if (process.env.NODE_ENV === 'development' || process.env.NODE_ENV === 'test') {
            encoding = hasOpus ? vibeProto.AudioEncoding.OPUS : vibeProto.AudioEncoding.LINEAR16;
        }
        if (IS_ELECTRON_APP_WITH_RECORDING) {
            encoding = vibeProto.AudioEncoding.OPUS;
        }

        let configError;
        let vendor, vendorConfiguration;

        try {
            [vendor, vendorConfiguration] = getEnabledASRVendor(asr || {});
            vendorConfiguration.lingwareTopic = lingwareTopic;
        } catch (err) {
            configError = err;
            recLog.error(`Failed to resolve ASR Vendor configuration, raw ASR configuration:${JSON.stringify(asr)}`);
        }

        return {
            initialConfig: {
                configError,
                enableAnonymization,
                enableAsrDebug,
                enableCallMetadataOnly,
                encoding,
                sampleRateHertz,
                asrVendors: { [vendor]: vendorConfiguration },
                maxAlternatives: 5,
                enableInverseNormalization: true,
                enableInterimResults: true,
                enablePhraseSpottingForInterimResults: true,
                languageCode,
                sessionInfo: {
                    source: proto.Source.MULTIPLE_CHANNELS,
                    sourceDescription: this._name,
                    callId,
                    callDetectionSessionId: this.getId(),
                    incallRecognitionSessionNumber
                },
                systemInfo
            }
        };
    }

    sendRecognitionConfig(options: SendRecognitionOptions) {
        const {
            languageCode,
            lingwareTopic,
            asr,
            sampleRateHertz,
            spottingConfig,
            systemInfo,
            enableAnonymization,
            enableAsrDebug,
            enableCallMetadataOnly,
            incallRecognitionSessionNumber,
            callId
        } = options;

        const recognitionConfig = this.buildRecognitionConfig({
            languageCode,
            lingwareTopic,
            asr,
            sampleRateHertz,
            systemInfo,
            enableAnonymization,
            incallRecognitionSessionNumber,
            enableAsrDebug,
            enableCallMetadataOnly,
            callId
        });
        recLog.log(`(${this._name}:${this._id}):: recoginiton config `, recognitionConfig);
        if (recognitionConfig.initialConfig.configError) {
            this.emit('asr_vendor_config_error');
            return;
        }
        this._send(recognitionConfig);

        if (!this._ws?.isOpen()) {
            const error = new Asr.CantUpdateSpottingConfigError({
                id: this._id,
                name: this._name
            });
            captureException(error);
            recLog.error(error.message, error);
            return;
        }

        if (!spottingConfig) {
            const error = new Asr.SpottingConfigRequiredError({
                id: this._id,
                name: this._name
            });
            captureException(error);
            recLog.error(error.message, error);
            return;
        }

        const request = {
            streamingRequest: {
                phraseSpottingConfig: spottingConfigToProto(spottingConfig)
            }
        };
        this._send(request);
        recLog.log(`(${this._name}:${this._id}):: spotting config request`, request);
    }

    _handleVoiceMetricsMessage = (voiceMetrics: Record<string, unknown> = {}) => {
        const { speechRatio, loudnessLufs: loudness, ourSideIsSpeaking, channel } = voiceMetrics;
        this.emit('voice_metrics', {
            loudness,
            speechRatio,
            ourSideIsSpeaking,
            channel
        });
    };

    _handleSpeechPaceMessage = (speechPaceMetrics: Record<string, unknown>) => {
        const { pace: speechPace, wordsPerMinute } = speechPaceMetrics;
        this.emit('speech_pace', {
            speechPace,
            wordsPerMinute
        });
    };

    _handleAsrResultMessage = (asrResult: unknown) => {
        if (asrResult == null) {
            recLog.log(`(${this._name}:${this._id}):: empty asr result`);
        }
        this.emit('asr_result', asrResult);
    };

    _handlePhraseSpottingResultMessage = (result: unknown) => {
        this.emit('phrase_spotting_result', result);
    };

    _handleMessage = (message: unknown) => {
        const callDetectionResult = get(message, 'resultsMessage.callDetectionResult');
        const voiceMetrics = get(message, 'resultsMessage.voiceMetrics');
        const speechPaceMetrics = get(message, 'resultsMessage.metrics.speechPaceMetrics');
        const asrResult = get(message, 'resultsMessage.asrResult');
        const phraseSpottingResult = get(message, 'resultsMessage.phraseSpottingResult');
        const serviceMessage = get(message, 'serviceMessage');

        if (callDetectionResult) {
            this.emit('call_detection_result', callDetectionResult);
        } else if (voiceMetrics) {
            this._handleVoiceMetricsMessage(voiceMetrics);
        } else if (speechPaceMetrics) {
            this._handleSpeechPaceMessage(speechPaceMetrics);
        } else if (asrResult) {
            this._handleAsrResultMessage(asrResult);
        } else if (phraseSpottingResult) {
            this._handlePhraseSpottingResultMessage(phraseSpottingResult);
        } else if (serviceMessage) {
            const status = vibeProto.ServiceMessageStatus[serviceMessage.status];

            if (serviceMessage.serviceIsFinalized && this._stopRequestedByClient) {
                this._serviceFinalized = true;
                recLog.log(`${this._prefix()}:: service finalized by Vibe`);
                this._ws?.close(this._stopCode ?? wsConnectionClosingCodes.DEFAULT);
                return;
            }

            if (status in holdEvents) {
                this.emit(holdEvents[status as keyof typeof holdEvents], {});
            } else if (status === startEvent && IS_UI_ONLY_MODE) {
                this.emit('call_start', { callID: serviceMessage.callId });
            }
        }
    };

    _reset() {
        recLog.log(`(${this._name}:${this._id}) (callId:${this._callId}):: call ended`);
        this._callId = null;
    }

    _handleOpen = noop;
}
